import { useMemo, useState, useEffect, useRef, Fragment } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Layers, Camera, ChevronDown, Search, Share2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { HoloStickerCard } from '../components/HoloStickerCard';
import { flagFor, specialIconFor, isCocaCola, pageMatchesQuery, codeMatchesQuery } from '../lib/flags';
import { useI18n } from '../lib/i18n';
import type { RarityTag } from '../lib/rarity';
import { ShareModal } from '../components/ShareModal';

export function Duplicates() {
  const { token } = useAuth();
  const { t } = useI18n();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [query, setQuery] = useState('');
  const [shareOpen, setShareOpen] = useState(false);

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(token!),
  });

  // Usamos la colección completa para tener page_title/page_number sin un endpoint extra
  const { data: col } = useQuery({
    queryKey: ['collection'],
    queryFn: () => api.collection(token!),
  });
  // Catalog para títulos de páginas sin stickers todavía
  const { data: detail } = useQuery({
    queryKey: ['album', 'mundial-2026'],
    queryFn: () => api.albumDetail('mundial-2026', token!),
  });

  const rarityMut = useMutation({
    mutationFn: ({ stickerId, rarityTag }: { stickerId: string; rarityTag: RarityTag | null }) =>
      api.setRarity(stickerId, rarityTag, token!),
    onMutate: async ({ stickerId, rarityTag }) => {
      await qc.cancelQueries({ queryKey: ['collection'] });
      const prev = qc.getQueryData<any>(['collection']);
      if (prev?.stickers) {
        const next = {
          ...prev,
          stickers: prev.stickers.map((s: any) =>
            s.id === stickerId ? { ...s, rarity_tag: rarityTag } : s,
          ),
        };
        qc.setQueryData(['collection'], next);
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['collection'], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['collection'] });
    },
  });

  const incMut = useMutation({
    mutationFn: ({ stickerId, delta }: { stickerId: string; delta: number }) =>
      api.incrementSticker(stickerId, delta, token!),
    onMutate: async ({ stickerId, delta }) => {
      await qc.cancelQueries({ queryKey: ['collection'] });
      const prev = qc.getQueryData<any>(['collection']);
      if (prev?.stickers) {
        const next = {
          ...prev,
          stickers: prev.stickers.map((s: any) =>
            s.id === stickerId ? { ...s, quantity: Math.max(0, (s.quantity ?? 0) + delta) } : s,
          ),
        };
        qc.setQueryData(['collection'], next);
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['collection'], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['collection'] });
      qc.invalidateQueries({ queryKey: ['duplicates'] });
      qc.invalidateQueries({ queryKey: ['missing'] });
      qc.invalidateQueries({ queryKey: ['matches'] });
    },
  });

  // Agrupar stickers con quantity >= 2 por página
  const groups = useMemo(() => {
    const dupes = (col?.stickers ?? []).filter((s: any) => s.quantity >= 2);
    if (dupes.length === 0) return [];
    const byPage = new Map<number, any[]>();
    for (const s of dupes) {
      if (!byPage.has(s.page_number)) byPage.set(s.page_number, []);
      byPage.get(s.page_number)!.push(s);
    }
    return [...byPage.entries()]
      .map(([pageNumber, items]) => {
        const meta = detail?.pages.find((p: any) => p.page_number === pageNumber);
        const title = meta?.title ?? items[0]?.page_title ?? `Página ${pageNumber}`;
        const sorted = items.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        const totalExtras = sorted.reduce((sum, s) => sum + (s.quantity - 1), 0);
        return { pageNumber, title, items: sorted, totalExtras };
      })
      .sort((a, b) => a.pageNumber - b.pageNumber);
  }, [col, detail]);

  // Búsqueda: prioridad país, luego jugador/code/número
  const searching = query.trim().length > 0;
  const q = query.trim().toLowerCase();
  const { countryMatches, playerMatches } = useMemo(() => {
    if (!searching) return { countryMatches: groups, playerMatches: [] as typeof groups };
    const cm = groups.filter((g) => pageMatchesQuery(g.title, q));
    const cmIds = new Set(cm.map((g) => g.pageNumber));
    const pm = groups
      .filter((g) => !cmIds.has(g.pageNumber))
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (s: any) =>
            String(s.number) === q ||
            codeMatchesQuery(s.code, q) ||
            (s.player_name?.toLowerCase().includes(q) ?? false),
        ),
      }))
      .filter((g) => g.items.length > 0);
    return { countryMatches: cm, playerMatches: pm };
  }, [groups, q, searching]);

  const filteredGroups = useMemo(
    () => [...countryMatches, ...playerMatches],
    [countryMatches, playerMatches],
  );

  // Auto-expand SOLO en la transición de empezar/parar de buscar.
  // (Sin esto, cualquier cambio de `filteredGroups` por mutación cierra la página)
  const prevSearching = useRef(false);
  useEffect(() => {
    if (searching && !prevSearching.current) {
      setExpanded(new Set(filteredGroups.map((g) => g.pageNumber)));
    } else if (!searching && prevSearching.current) {
      setExpanded(new Set());
    }
    prevSearching.current = searching;
  }, [searching, filteredGroups]);

  function toggleExpand(pn: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(pn) ? next.delete(pn) : next.add(pn);
      return next;
    });
  }

  const totalExtras = groups.reduce((sum, g) => sum + g.totalExtras, 0);

  return (
    <div className="space-y-5">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-2xs uppercase tracking-widest text-haze">{t('dupes.eyebrow')}</p>
          <h1 className="font-display font-extrabold text-3xl">
            {t('dupes.title')} <span className="text-cyan">{totalExtras}</span>
          </h1>
          <p className="mt-1 text-sm text-haze">{t('dupes.subtitle')}</p>
        </div>
        {me?.shareSlug && totalExtras > 0 && (
          <button
            onClick={() => setShareOpen(true)}
            aria-label="share dupes"
            className="shrink-0 mt-1 h-11 w-11 grid place-items-center rounded-full bg-lime text-ink shadow-glow-lime hover:scale-110 transition"
          >
            <Share2 size={18} strokeWidth={2.5} />
          </button>
        )}
      </header>

      {me?.shareSlug && (
        <ShareModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          shareSlug={me.shareSlug}
          displayName={me.displayName}
          mode="duplicates"
        />
      )}

      {/* CTA: subir foto de repes sueltas */}
      <Link
        to="/app/upload-loose"
        className="relative overflow-hidden block rounded-2xl glass-strong p-4 flex items-center gap-3 hover:bg-graphite/40 transition group"
      >
        <div className="h-11 w-11 rounded-xl grid place-items-center bg-cyan/15 text-cyan border border-cyan/40 group-hover:bg-cyan/25 transition shrink-0">
          <Layers size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-extrabold text-base leading-tight flex items-center gap-2">
            {t('dupes.uploadCta')}
            <Camera size={14} className="text-cyan" />
          </div>
          <div className="text-2xs text-haze mt-0.5">{t('dupes.uploadCtaDesc')}</div>
        </div>
        <div className="pointer-events-none absolute -top-8 -right-8 h-24 w-24 rounded-full bg-cyan/20 blur-2xl group-hover:bg-cyan/35 transition" />
      </Link>

      {/* Search bar — solo si hay repes */}
      {groups.length > 0 && (
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-haze" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('common.searchPlaceholder')}
            className="input pl-9"
          />
        </div>
      )}

      {/* Lista de países con repes */}
      {groups.length === 0 ? (
        <div className="glass rounded-2xl p-8 text-center">
          <p className="text-haze">{t('dupes.empty')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {searching && countryMatches.length > 0 && (
            <h3 className="text-2xs uppercase tracking-widest text-haze font-bold ml-1 mt-1">
              {t('album.searchCountries')} · {countryMatches.length}
            </h3>
          )}
          {filteredGroups.length === 0 && searching && (
            <div className="glass rounded-2xl p-8 text-center text-haze text-sm">
              {t('album.noResults')} "{query}".
            </div>
          )}
          {filteredGroups.map((g, idx) => {
            const isOpen = expanded.has(g.pageNumber);
            const flag = flagFor(g.title);
            // Insertar el separador "Jugador · N" antes del primer match de jugador
            const isFirstPlayerMatch =
              searching &&
              playerMatches.length > 0 &&
              idx === countryMatches.length;
            return (
              <Fragment key={g.pageNumber}>
                {isFirstPlayerMatch && (
                  <h3 key="player-header" className="text-2xs uppercase tracking-widest text-haze font-bold ml-1 mt-3">
                    {t('album.searchPlayers')} · {playerMatches.reduce((s, g2) => s + g2.items.length, 0)}
                  </h3>
                )}
              <div
                className="glass-strong rounded-2xl border border-cyan/30 shadow-[0_0_18px_-12px_rgba(94,224,255,0.5)] overflow-hidden"
              >
                <button
                  onClick={() => toggleExpand(g.pageNumber)}
                  className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.02] transition"
                >
                  {isCocaCola(g.title) ? (
                    <div className="shrink-0 grid h-12 w-12 rounded-xl place-items-center bg-flame border-2 border-flame font-display font-extrabold text-2xl text-white shadow-glow-flame italic">
                      C
                    </div>
                  ) : (
                    <div className="shrink-0 grid h-12 w-12 rounded-xl place-items-center text-2xl bg-cyan/15 border border-cyan/30">
                      {flag ?? specialIconFor(g.title) ?? '✦'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-display font-extrabold text-base truncate">
                        {g.title}
                      </h3>
                      <span className="text-2xs font-bold tabular-nums shrink-0 text-cyan">
                        ×{g.totalExtras}
                      </span>
                    </div>
                    <div className="text-2xs text-haze mt-0.5">
                      {g.items.length} {g.items.length === 1 ? t('common.from').replace('de', 'sticker') : 'stickers'}
                      {' · '}{g.totalExtras} {g.totalExtras === 1 ? t('album.dupes') : t('album.dupesPlural')}
                    </div>
                  </div>
                  <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.25 }}
                    className="shrink-0 text-haze"
                  >
                    <ChevronDown size={20} />
                  </motion.div>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: 'easeOut' }}
                      className="overflow-hidden"
                    >
                      <div className="px-3 pb-4 pt-1 border-t border-line/40">
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
                          {g.items.map((s: any) => (
                            <HoloStickerCard
                              key={s.id}
                              number={s.number}
                              code={s.code}
                              rarity={s.rarity}
                              state="duplicate"
                              duplicates={s.quantity - 1}
                              playerName={s.player_name}
                              team={s.team}
                              imageUrl={s.image_url}
                              size="md"
                              rarityTag={s.rarity_tag ?? null}
                              onRarityChange={(tag) => rarityMut.mutate({ stickerId: s.id, rarityTag: tag })}
                              onIncrement={() => incMut.mutate({ stickerId: s.id, delta: 1 })}
                              onDecrement={() => incMut.mutate({ stickerId: s.id, delta: -1 })}
                            />
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
